import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppErrorBoundary } from './components/app-error-boundary';
import { AnalyticsListener } from './components/analytics-listener';
import { AppShell } from './components/app-shell';
import { useFrpSession, FrpSessionProvider } from './features/session/frp-session-context';

// Route-level lazy loading for per-route code splits
const HomePage        = lazy(async () => ({ default: (await import('./pages/home-page')).HomePage }));
const LogJobPage      = lazy(async () => ({ default: (await import('./pages/log-job-page')).LogJobPage }));
const JobsPage        = lazy(async () => ({ default: (await import('./pages/jobs-page')).JobsPage }));
const EarningsPage    = lazy(async () => ({ default: (await import('./pages/earnings-page')).EarningsPage }));
const AnalyticsPage   = lazy(async () => ({ default: (await import('./pages/analytics-page')).AnalyticsPage }));
const IntegrationsPage = lazy(async () => ({ default: (await import('./pages/integrations-page')).IntegrationsPage }));
const SettingsPage    = lazy(async () => ({ default: (await import('./pages/settings-page')).SettingsPage }));
const PortfolioPage   = lazy(async () => ({ default: (await import('./pages/portfolio-page')).PortfolioPage }));
const AssignmentsPage = lazy(async () => ({ default: (await import('./pages/assignments-page')).AssignmentsPage }));
const SetupPage       = lazy(async () => ({ default: (await import('./pages/setup-page')).SetupPage }));
const EditJobPage     = lazy(async () => ({ default: (await import('./pages/edit-job-page')).EditJobPage }));
const SignInPage           = lazy(async () => ({ default: (await import('./pages/sign-in-page')).SignInPage }));
const TimeTrackingPage     = lazy(async () => ({ default: (await import('./pages/time-tracking-page')).TimeTrackingPage }));
const ToolInventoryPage    = lazy(async () => ({ default: (await import('./pages/tool-inventory-page')).ToolInventoryPage }));
const TrainingPage         = lazy(async () => ({ default: (await import('./pages/training-page')).TrainingPage }));

function RouteLoadingFallback() {
  return (
    <div className="frp-loading">
      <div className="frp-spinner" />
      <span>Loading…</span>
    </div>
  );
}

function BootFallback() {
  return (
    <div className="frp-boot">
      <div className="frp-boot-card">
        <div className="frp-spinner" />
        <p className="frp-boot-title">FlatRate Pro</p>
        <p className="frp-boot-copy">
          Loading your technician workspace…
        </p>
      </div>
    </div>
  );
}

/** Auth guard — redirects to /setup if profile incomplete, /sign-in if unauthenticated */
function AuthGuard({ children }: { children: React.ReactElement }) {
  const session = useFrpSession();

  if (session.status === 'loading') {
    return <BootFallback />;
  }

  if (session.status === 'unauthenticated') {
    return <Navigate to="/sign-in" replace />;
  }

  if (session.status === 'error') {
    return (
      <div className="frp-boot">
        <div className="frp-boot-card">
          <p className="frp-eyebrow frp-eyebrow-danger">Error</p>
          <p className="frp-boot-title">Could not connect</p>
          <p className="frp-boot-copy">
            {session.error || 'An unexpected error occurred.'}
          </p>
          <button
            type="button"
            className="frp-btn frp-btn-primary frp-btn-full frp-margin-top-4"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Authenticated: check if setup is complete
  if (!session.profile?.setupComplete) {
    return <Navigate to="/setup" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        {/* Setup — no shell, no auth guard required */}
        <Route
          path="/setup"
          element={<SetupPage />}
        />

        {/* Main app shell with bottom nav + FAB */}
        <Route
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/home" replace />} />
          <Route path="/home"        element={<HomePage />} />
          <Route path="/jobs"        element={<JobsPage />} />
          <Route path="/earnings"    element={<EarningsPage />} />
          <Route path="/analytics"   element={<AnalyticsPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/settings"    element={<SettingsPage />} />
          <Route path="/portfolio"   element={<PortfolioPage />} />
          <Route path="/assignments" element={<AssignmentsPage />} />
          <Route path="/time"        element={<TimeTrackingPage />} />
          <Route path="/tools"       element={<ToolInventoryPage />} />
          <Route path="/training"    element={<TrainingPage />} />
        </Route>

        {/* Log Job — full-screen modal-style, no bottom nav */}
        <Route
          path="/log-job"
          element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <LogJobPage />
              </Suspense>
            </AuthGuard>
          }
        />

        {/* Edit Job — full-screen, no bottom nav */}
        <Route
          path="/edit-job/:jobId"
          element={
            <AuthGuard>
              <Suspense fallback={<RouteLoadingFallback />}>
                <EditJobPage />
              </Suspense>
            </AuthGuard>
          }
        />

        {/* Sign in — no auth required */}
        <Route path="/sign-in" element={<SignInPage />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <FrpSessionProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <AnalyticsListener />
          <AppRoutes />
        </BrowserRouter>
      </FrpSessionProvider>
    </AppErrorBoundary>
  );
}
